import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import {
  SENDER_ENDPOINTS,
  USER_ENDPOINTS,
  getEndpointInfo,
} from './apiEndPoints';
// Types
interface UpdateUserRoleRequest {
  user_id: number;
  role_id: number;
}

interface UpdateProfileRequest {
  fullName: string;
  birthday: string;
  contactNumber: string;
  address: {
    country_id: number;
    state_id: number;
    city_id: number;
    postalCode: string;
    street1: string;
    street2?: string;
  };
}

// Time Preferences Interfaces
interface UpdateTimePreferencesRequest {
  user_id: number;
  sender_drop_offset: number;
  receiver_pick_offset: number;
}

// Distance Preferences Interfaces
// KYC QR Code Interfaces
export interface GenerateKycQRCodeRequest {
  user_id: string;
  country: string;
}

export interface GenerateKycQRCodeResponse {
  success: boolean;
  data: {
    sessionId: string;
    qrCode: string;
    url: string;
    status: string;
  };
}

// Query keys
export const userKeys = {
  all: ['user'] as const,
  // Collections - use these for invalidating all items of a type
  allProfiles: ['user', 'profile'] as const,
  allTimePreferences: ['user', 'timePreferences'] as const,
  allDistancePreferences: ['user', 'distancePreferences'] as const,
  // Individual entities - use these for specific queries
  profile: (auth0Id: string) => ['user', 'profile', auth0Id] as const,
  countries: ['user', 'countries'] as const,
  states: (countryId: string) => ['user', 'states', countryId] as const,
  cities: (stateId: string) => ['user', 'cities', stateId] as const,
  airports: (search: string) => ['user', 'airports', search] as const,
  weightPrices: ['user', 'weightPrices'] as const,
  timePreferences: (userId: number) => ['user', 'timePreferences', userId] as const,
  distancePreferences: (userId: number) => ['user', 'distancePreferences', userId] as const,
  kycQRCode: ['user', 'kycQRCode'] as const,
};

// API functions
const userApiClient = {
  // Base query function
  async request({ url, method = 'GET', body, headers }: {
    url: string;
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
  }) {
    try {
      const userService = getApiService(ServiceType.USER);
      const response = await userService({
        url,
        method,
        data: body,
        headers: {
          'Content-Type': method === 'POST' || method === 'PUT' ? 'application/json' : undefined,
          ...headers,
        },
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  // API endpoint implementations
  getUserProfile: (auth0Id: string) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_USER_PROFILE);
    return userApiClient.request({
      url: endpoint.path.replace(':auth0Id', auth0Id),
    });
  },

  getCountries: () => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_COUNTRIES);
    return userApiClient.request({
      url: endpoint.path,
    });
  },

  getStates: (countryId: string) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_STATES);
    return userApiClient.request({
      url: endpoint.path.replace(':countryId', countryId),
    });
  },

  getCities: (stateId: string) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_CITIES);
    return userApiClient.request({
      url: endpoint.path.replace(':stateId', stateId),
    });
  },

  updateUserProfile: ({ userId, data }: { userId: number; data: UpdateProfileRequest }) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.UPDATE_USER_PROFILE);
    return userApiClient.request({
      url: endpoint.path.replace(':userId', userId.toString()),
      method: 'PUT',
      body: data,
    });
  },

  updateUserRole: (data: UpdateUserRoleRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.CHANGE_USER_ROLE);
    const payload = {
      user_id: Number(data.user_id),
      role_id: Number(data.role_id),
    };

    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: payload,
    });
  },

  getAirports: (search: string) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.GET_AIRPORTS.path.replace(
        ':search',
        encodeURIComponent(search)
      ),
    });
  },

  uploadItemImages: (data: unknown) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.UPLOAD_ITEM_IMAGES.path,
      method: 'POST',
      body: data,
    });
  },

  uploadIdCard: (payload: unknown) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.UPLOAD_ID_CARD.path,
      method: 'POST',
      body: payload,
    });
  },

  uploadItemImagesForSender: (payload: unknown) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.UPLOAD_ITEM_IMAGES.path,
      method: 'POST',
      body: payload,
    });
  },
  uploadImagesForSender: (payload: unknown) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.UPLOAD_IMAGES.path,
      method: 'POST',
      body: payload,
    });
  },

  updatePassword: (data: { userId: string; oldPassword: string; newPassword: string }) => {
    return userApiClient.request({
      url: USER_ENDPOINTS.UPDATE_PASSWORD.path,
      method: 'POST',
      body: data,
    });
  },

  deleteAccount: () => {
    return userApiClient.request({
      url: USER_ENDPOINTS.DELETE_ACCOUNT.path,
      method: 'DELETE',
    });
  },

  // Time Preferences
  getTimePreferences: (userId: number) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_TIME_PREFERENCE);
    return userApiClient.request({
      url: endpoint.path.replace(':userId', userId.toString()),
    }).then(response => response?.data || response);
  },

  updateTimePreferences: (data: UpdateTimePreferencesRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.UPDATE_TIME_PREFERENCE);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: data,
    }).then(response => response?.data || response);
  },

  // Distance Preferences
  getDistancePreferences: (userId: number) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_DISTANCE_PREFERENCE);
    return userApiClient.request({
      url: endpoint.path.replace(':userId', userId.toString()),
    }).then(response => {
      const data = response?.data || response;
      return {
        ...data,
        distance_unit: data.distance_unit?.toUpperCase() || 'KM',
      };
    });
  },


  // KYC QR Code
  generateKycQRCode: (data: GenerateKycQRCodeRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.KYC);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: data,
    });
  },

  getShipmentCategories: () => {
    return userApiClient.request({
      url: SENDER_ENDPOINTS.GET_SHIPMENT_CATEGORIES.path,
    });
  },
};

// TanStack Query hooks
export const useGetUserProfileQuery = (auth0Id: string, options = {}) =>
  useQuery({
    queryKey: userKeys.profile(auth0Id),
    queryFn: () => userApiClient.getUserProfile(auth0Id),
    ...options,
  });

export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: UpdateProfileRequest }) =>
      userApiClient.updateUserProfile({ userId, data }),
    onSuccess: () => {
      // Invalidate profile query
      queryClient.invalidateQueries({ queryKey: userKeys.allProfiles });
    },
  });
};

export const useUpdateUserRoleMutation = () => {
  return useMutation({
    mutationFn: (data: UpdateUserRoleRequest) => userApiClient.updateUserRole(data),
  });
};

export const useGetAirportsQuery = (search: string, options = {}) =>
  useQuery({
    queryKey: userKeys.airports(search),
    queryFn: () => userApiClient.getAirports(search),
    enabled: !!search,
    ...options,
  });

export const useUploadItemImagesMutation = () => {
  return useMutation({
    mutationFn: (data: unknown) => userApiClient.uploadItemImages(data),
  });
};


export const useUploadImagesForSenderMutation = () => {
  return useMutation({
    mutationFn: (payload: unknown) => userApiClient.uploadImagesForSender(payload),
  });
};

export const useUpdatePasswordMutation = () => {
  return useMutation({
    mutationFn: (data: { userId: string; oldPassword: string; newPassword: string }) =>
      userApiClient.updatePassword(data),
  });
};

export const useDeleteAccountMutation = () => {
  return useMutation({
    mutationFn: () => userApiClient.deleteAccount(),
  });
};

export const useGetDistancePreferencesQuery = (userId: number, options = {}) =>
  useQuery({
    queryKey: userKeys.distancePreferences(userId),
    queryFn: () => userApiClient.getDistancePreferences(userId),
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes (user preferences don't change frequently)
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnMount: false, // Don't refetch on component mount if data is cached
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    ...options,
  });

export const useGetShipmentCategoriesQuery = (options = {}) =>
  useQuery({
    queryKey: ['sender', 'shipmentCategories'] as const,
    queryFn: () => userApiClient.getShipmentCategories(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    ...options,
  });

export const useGenerateKycQRCodeMutation = () => {
  return useMutation({
    mutationFn: (data: GenerateKycQRCodeRequest) => userApiClient.generateKycQRCode(data),
  });
};